'use client';

import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { SvgXMark } from '../Icons/XMark';

// Define prop types
interface ModalProps {
	isOpen: boolean;
	onClose: () => void;
	children: ReactNode;
	title?: ReactNode;
	size?: 'sm' | 'md' | 'lg' | 'xl';
	closeOnOverlayClick?: boolean;
	showCloseIcon?: boolean;
	className?: string;
}

export const Modal = ({
	isOpen,
	onClose,
	children,
	title,
	size = 'md',
	closeOnOverlayClick = true,
	showCloseIcon = true,
	className,
}: ModalProps) => {
	// Handle escape key press
	useEffect(() => {
		const handleEsc = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && isOpen) {
				onClose();
			}
		};
		window.addEventListener('keydown', handleEsc);
		return () => window.removeEventListener('keydown', handleEsc);
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	// Modal size variations
	const sizeClasses = {
		sm: 'max-w-sm',
		md: 'max-w-md',
		lg: 'max-w-lg',
		xl: 'max-w-xl',
	};

	return (
		<div className="absolute inset-0 z-50 overflow-y-auto fade-in">
			{/* Overlay */}
			<div
				className="absolute inset-0 bg-[var(--color-base-black)]/50 transition-opacity h-full"
				onClick={closeOnOverlayClick ? onClose : undefined}
				onKeyDown={() => {}}
				aria-hidden="true"
			/>

			{/* Modal Content */}
			<div className="flex h-full items-center justify-center">
				<div
					className={twMerge(
						'relative w-full bg-white rounded-lg shadow-2xl transform transition-all mx-auto p-5 pb-8 flex flex-col gap-5',
						sizeClasses[size],
						className,
					)}
				>
					{/* Header */}
					<div className="flex items-center justify-between">
						{/* Title Section */}
						{title}
						{/* Close Button Section */}
						{showCloseIcon && (
							<button
								onClick={onClose}
								className="text-[var(--color-base-black)] hover:text-gray-700 cursor-pointer hover:bg-gray-100 rounded-full p-1"
								aria-label="Close modal"
								type="button"
							>
								<SvgXMark />
							</button>
						)}
					</div>

					{/* Content */}
					<div>{children}</div>
				</div>
			</div>
		</div>
	);
};

export default Modal;
